"use client";

import React, { useState } from "react";
import { MapPin, Clock, ShieldCheck, Zap, ArrowRight, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ServingNorfolkBeyond() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeLocation, setActiveLocation] = useState<string | null>(null);
  const locations = [{
    name: "Norwich",
    x: 65,
    y: 45,
    main: true
  }, {
    name: "King's Lynn",
    x: 25,
    y: 40,
    main: false
  }, {
    name: "Great Yarmouth",
    x: 85,
    y: 50,
    main: false
  }, {
    name: "Thetford",
    x: 45,
    y: 75,
    main: false
  }, {
    name: "Cromer",
    x: 60,
    y: 15,
    main: false
  }, {
    name: "Fakenham",
    x: 45,
    y: 30,
    main: false
  }];
  const coreValues = [{
    icon: <Clock className="size-5 text-primary" />,
    title: "24/7 Availability",
    desc: "Emergency and non-emergency services at any time."
  }, {
    icon: <Zap className="size-5 text-primary" />,
    title: "Rapid Response",
    desc: "Fast mobile service direct to your location."
  }, {
    icon: <ShieldCheck className="size-5 text-primary" />,
    title: "Trusted Service",
    desc: "Certified and experienced locksmiths you can rely on."
  }];
  return <section id="serving-norfolk-beyond" ref={ref} className={`bg-background py-24 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {}
          <div className="lg:col-span-2 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Navigation className="size-4" />
                <span>Service Area</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Covering Every Corner of <span className="text-primary">Norfolk</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                AngliaKeys is a professional car auto locksmith serving Norfolk and East Anglia. 
                We are dedicated to rapid assistance for drivers facing lock‑out emergencies, 
                bringing advanced key‑cutting technology directly to where you are stranded.
              </p>
            </div>

            <div className="space-y-6">
              {coreValues.map((value, i) => <div key={i} data-index={i} className="flex gap-4 items-start group">
                  <div className="mt-1 flex-shrink-0 p-2 rounded-lg bg-secondary/50 group-hover:bg-primary/20 transition-colors">
                    {value.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto group">
                <Link to="/AreasCoveredPage">
                  View Full Coverage Map
                  <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-3 relative">
            <Card className="relative overflow-hidden border-border/50 bg-secondary/5 shadow-2xl backdrop-blur-sm p-6 md:p-10">
              {}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

              <div className="relative aspect-[4/3] w-full">
                {}
                <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-xl" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {}
                  <path d="M20,40 Q25,20 45,15 T75,20 T90,45 T85,70 T50,85 T25,75 T15,55 Z" className="fill-card stroke-border stroke-2" />
                  
                  {}
                  <circle cx="65" cy="45" r="25" className="fill-primary/5 animate-pulse" />
                  <circle cx="65" cy="45" r="15" className="fill-primary/10 animate-ping opacity-75" style={{
                  animationDuration: '3s'
                }} />

                  {}
                  <path d="M65,45 L25,40" className="stroke-primary/20 stroke-1 stroke-dasharray-2" />
                  <path d="M65,45 L85,50" className="stroke-primary/20 stroke-1 stroke-dasharray-2" />
                  <path d="M65,45 L45,75" className="stroke-primary/20 stroke-1 stroke-dasharray-2" />
                  <path d="M65,45 L60,15" className="stroke-primary/20 stroke-1 stroke-dasharray-2" />
                  <path d="M65,45 L45,30" className="stroke-primary/20 stroke-1 stroke-dasharray-2" />

                </svg>

                {}
                <div className="absolute inset-0">
                  <TooltipProvider>
                    {locations.map((loc, i) => <div key={i} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group" style={{
                    left: `${loc.x}%`,
                    top: `${loc.y}%`
                  }} onMouseEnter={() => setActiveLocation(loc.name)} onMouseLeave={() => setActiveLocation(null)}>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <div className="relative flex items-center justify-center">
                              {}
                              <span className={`absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping ${loc.main ? 'size-8' : 'size-4'}`}></span>
                              
                              {}
                              <div className={`relative inline-flex items-center justify-center rounded-full bg-background border-2 border-primary shadow-lg transition-transform duration-300 group-hover:scale-110 ${loc.main ? 'size-8' : 'size-4'}`}>
                                {loc.main && <MapPin className="size-4 text-primary fill-primary/20" />}
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-primary text-primary-foreground font-bold border-none shadow-xl">
                            <p>{loc.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>)}
                  </TooltipProvider>
                </div>

                {}
                <div className="absolute bottom-4 left-4 bg-card/90 backdrop-blur border border-border p-3 rounded-lg shadow-lg text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="block size-2 rounded-full bg-primary animate-pulse"></span>
                    <span className="font-semibold text-foreground">Active Service Area</span>
                  </div>
                  <div className="text-muted-foreground">
                    Avg. Response: <span className="text-foreground font-medium">30-60 mins</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}